import { Controller } from '@hotwired/stimulus'
import { TurboSubmitEndEvent } from '@hotwired/turbo'

type AnimationDirection = 'normal' | 'reverse'

export default class ModalController extends Controller<HTMLElement> {
  declare skipTransitionValue: boolean
  declare readonly backdropTarget: HTMLDivElement
  declare readonly panelTarget: HTMLDivElement

  static targets = ['backdrop', 'panel']
  static values = { skipTransition: { default: false, type: Boolean} }

  connect() {
    this.open()
    this.element.addEventListener('submit', this.#handleSubmit.bind(this))
    this.element.addEventListener('turbo:submit-end', this.#handleSubmitEnd.bind(this))
  }

  disconnect() {
    this.element.removeEventListener('submit', this.#handleSubmit.bind(this))
    this.element.removeEventListener('turbo:submit-end', this.#handleSubmitEnd.bind(this))
  }

  // Public methods

  close() {
    this.dispatch('close')
    this.#animateBackdrop('reverse')
    this.#animatePanel('reverse')
    this.#finishAnimating().then(() => {
      this.dispatch('closed')
      this.element.remove()
    })
  }

  open() {
    this.dispatch('open')
    this.#animateBackdrop('normal')
    this.#animatePanel('normal')
    this.#finishAnimating().then(() => this.dispatch('opened'))
  }

  // Private methods

  #animateBackdrop(direction: AnimationDirection) {
    const options = this.#animationOptions(direction)

    this.backdropTarget.animate([{opacity: 0}, {opacity: 1}], options)
  }

  #animatePanel(direction: AnimationDirection) {
    const options = this.#animationOptions(direction)

    this.panelTarget.animate([
      {opacity: 0, transform: 'translateY(1rem) scale(0.95)'},
      {opacity: 1, transform: 'translateY(0) scale(1)'}
    ], options)
  }

  #animationOptions(direction: AnimationDirection) {
    const isClosing = direction === 'reverse'


    return {
      direction,
      duration: isClosing ? 150 : this.skipTransitionValue ? 0 : 300,
      easing: isClosing ? 'ease-in' : 'ease-out',
      fill: 'both'
    } satisfies KeyframeEffectOptions
  }

  #finishAnimating() {
    return Promise.allSettled(
      this.element.getAnimations({ subtree: true }).map((animation) =>
        animation.finished
      ),
    )
  }

  #handleSubmit() {
    this.dispatch('submit')
  }

  /**
   * Turbo captures the form submission and handles the response as a
   * Turbo Stream. So, even though we're redirecting in the controller, the
   * redirect won't happen. We've discussed the issue as a team and, sometime
   * in the future, will resolve it by responding via turbo stream in the view.
   * That will necessitate that we rebuild the view via some partials or Phlex
   * components, which is out of scope for the work at hand. For now, we'll
   * just inspect the `turbo:submit-end` event and manually redirect if needed.
   */
  #handleSubmitEnd({ detail }: TurboSubmitEndEvent) {
    const { redirected, response, succeeded } = detail.fetchResponse

    if (succeeded && redirected) {
      window.location.assign(response.url)
    }
  }
}
